/**
 * Six hero KPI tiles for the Overview page, rendered with the fleetGUARDIAN
 * `<HeroKpi>` card (count-up value + icon tile + optional delta pill / link).
 *
 * Every value is a SERVER-computed aggregate from `/overview/kpis` — nothing is
 * summed client-side. Tiles 2 (Spend) and 4 (Total Litres) carry a period-over-
 * period delta pill; the others show a subtitle/hint line instead.
 *
 *  1. Active Fleet — current count + "+N added this period"
 *  2. Spend        — period total + delta % vs prior period
 *  3. Avg R/L      — weighted-average price (with sample size)
 *  4. Total Litres — period sum + delta % vs prior period
 *  5. Open Critical— count, links to the Fraud Hub
 *  6. Fraud Exposure — open_critical / active_fleet, as %
 */

import {
  AlertTriangle,
  Droplets,
  Fuel,
  ShieldAlert,
  Truck,
  Wallet,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { HeroKpi } from '@/components/fleetguardian';
import { fmtL, fmtNum, fmtPct, fmtR, fmtR2 } from '@/components/fleetguardian/format';
import type { OverviewKpisResponse } from '@/types/overview';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });

export interface HeroKpiTilesProps {
  data: OverviewKpisResponse | undefined;
  isLoading: boolean;
  /** Reflects the page's R/litre weighted toggle (drives the hint copy). */
  weighted?: boolean;
}

export function HeroKpiTiles({
  data,
  isLoading,
  weighted = true,
}: HeroKpiTilesProps): JSX.Element {
  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-[132px] w-full rounded-xl" />
        ))}
      </div>
    );
  }

  // Prefer the KpiMetric envelope (honours the ?weighted toggle server-side);
  // fall back to the flat WeightedAvg for resilience against stale caches.
  const rplMetric = data.metrics?.avg_rand_per_litre;
  const avgValue = rplMetric ? rplMetric.value : data.avg_rand_per_litre.value;
  const avgKnown = avgValue != null;
  const avgSampleN = rplMetric?.sample_n ?? data.avg_rand_per_litre.sample_n;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {/* 1. Active Fleet */}
      <HeroKpi
        label="Active Fleet"
        icon={Truck}
        hue="blue"
        value={data.active_fleet}
        format={fmtNum}
        hint={
          data.fleet_added_this_period > 0
            ? `+${NUM.format(data.fleet_added_this_period)} added this period`
            : 'no new vehicles this period'
        }
      />

      {/* 2. Spend */}
      <HeroKpi
        label="Spend"
        icon={Wallet}
        hue="emerald"
        value={data.spend}
        format={fmtR}
        delta={data.spend_delta_pct ?? null}
      />

      {/* 3. Avg R/L (weighted toggle) */}
      <HeroKpi
        label="Avg R/L"
        icon={Fuel}
        hue="indigo"
        value={avgKnown ? (avgValue as number) : 0}
        countUp={avgKnown}
        format={(v) => (avgKnown ? fmtR2(v) : '—')}
        hint={
          weighted
            ? `weighted · n=${NUM.format(avgSampleN)} txns`
            : 'simple mean of unit price'
        }
      />

      {/* 4. Total Litres */}
      <HeroKpi
        label="Total Litres"
        icon={Droplets}
        hue="amber"
        value={data.total_litres}
        format={fmtL}
        delta={data.total_litres_delta_pct ?? null}
      />

      {/* 5. Open Critical Alerts */}
      <HeroKpi
        label="Open Critical"
        icon={AlertTriangle}
        hue="red"
        value={data.open_critical}
        format={fmtNum}
        to="/fraud-hub"
        hint="Investigate now →"
      />

      {/* 6. Fraud Exposure % */}
      <HeroKpi
        label="Fraud Exposure"
        icon={ShieldAlert}
        hue="orange"
        value={data.fraud_exposure_pct}
        format={(v) => fmtPct(v, 1)}
        hint="open Critical ÷ active fleet"
      />
    </div>
  );
}

export default HeroKpiTiles;
